/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Content 1 PD component — image + copy side-by-side editorial block
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';
import {
    FULL_IMAGE_CLASSES,
    getAlignClasses,
    getMarginBottomClass,
    getTextStyle,
    isFullImage,
} from '@/lib/page-designer/style-attributes';

/* v8 ignore start */
@Component('contentOne', {
    name: 'Content 1',
    description:
        'Editorial split block: image on one side, eyebrow + title + body copy + two CTAs + legal text on the other.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ContentOneMetadata {
    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow / Tagline', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'bodyCopy', name: 'Body Copy', type: 'text' })
    bodyCopy?: string;

    @AttributeDefinition({ id: 'ctaOneText', name: 'Primary CTA Text', type: 'string' })
    ctaOneText?: string;

    @AttributeDefinition({ id: 'ctaOneLink', name: 'Primary CTA Link', type: 'url' })
    ctaOneLink?: string;

    @AttributeDefinition({ id: 'ctaTwoText', name: 'Secondary CTA Text', type: 'string' })
    ctaTwoText?: string;

    @AttributeDefinition({ id: 'ctaTwoLink', name: 'Secondary CTA Link', type: 'url' })
    ctaTwoLink?: string;

    @AttributeDefinition({ id: 'legalText', name: 'Legal Text', type: 'string' })
    legalText?: string;

    @AttributeDefinition({ name: 'Image', type: 'image' })
    image?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;

    @AttributeDefinition({
        id: 'orientation',
        name: 'Orientation',
        description: 'Which side the image appears on',
        type: 'enum',
        values: ['imageRight', 'imageLeft'],
        defaultValue: 'imageRight',
    })
    orientation?: string;

    @AttributeDefinition({
        id: 'headingFontStyle',
        name: 'Style – Heading Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    headingFontStyle?: string;

    @AttributeDefinition({
        id: 'headingFontSize',
        name: 'Style – Heading Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    headingFontSize?: string;

    @AttributeDefinition({
        id: 'headingColor',
        name: 'Style – Heading Color',
        type: 'string',
        description: 'Hex color, e.g. #1A1A1A or #fff. Leave blank for the default.',
    })
    headingColor?: string;

    @AttributeDefinition({
        id: 'bodyFontStyle',
        name: 'Style – Body Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    bodyFontStyle?: string;

    @AttributeDefinition({
        id: 'bodyFontSize',
        name: 'Style – Body Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    bodyFontSize?: string;

    @AttributeDefinition({
        id: 'bodyColor',
        name: 'Style – Body Color',
        type: 'string',
        description: 'Hex color, e.g. #1A1A1A or #fff. Leave blank for the default.',
    })
    bodyColor?: string;

    @AttributeDefinition({
        id: 'textAlign',
        name: 'Style – Text Alignment',
        type: 'enum',
        values: ['Default', 'Left', 'Center', 'Right'],
        defaultValue: 'Default',
    })
    textAlign?: string;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Style – Margin Bottom',
        type: 'enum',
        values: ['0', '2', '4', '6', '8', '12', '16'],
        defaultValue: '0',
        description: 'Space below the component (Tailwind spacing scale; 4 = 16px).',
    })
    marginBottom?: string;

    @AttributeDefinition({
        id: 'imageDisplay',
        name: 'Style – Image Display',
        type: 'enum',
        values: ['Crop to fit', 'Show full image'],
        defaultValue: 'Crop to fit',
        description: 'Show full image keeps the whole image at its natural height (no top/bottom crop).',
    })
    imageDisplay?: string;
}
/* v8 ignore stop */

export default function ContentOne({
    eyebrow,
    title,
    bodyCopy,
    ctaOneText,
    ctaOneLink,
    ctaTwoText,
    ctaTwoLink,
    legalText,
    image,
    imageAlt,
    orientation,
    headingFontStyle,
    headingFontSize,
    headingColor,
    bodyFontStyle,
    bodyFontSize,
    bodyColor,
    textAlign,
    marginBottom,
    imageDisplay,
}: {
    eyebrow?: string;
    title?: string;
    bodyCopy?: string;
    ctaOneText?: string;
    ctaOneLink?: string;
    ctaTwoText?: string;
    ctaTwoLink?: string;
    legalText?: string;
    image?: Image;
    imageAlt?: string;
    orientation?: string;
    headingFontStyle?: string;
    headingFontSize?: string;
    headingColor?: string;
    bodyFontStyle?: string;
    bodyFontSize?: string;
    bodyColor?: string;
    textAlign?: string;
    marginBottom?: string;
    imageDisplay?: string;
}) {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-80px' });
    const imageRight = !orientation || orientation === 'imageRight';
    const heading = getTextStyle(
        { fontStyle: headingFontStyle, fontSize: headingFontSize, color: headingColor },
        'heading'
    );
    const bodyText = getTextStyle({ fontStyle: bodyFontStyle, fontSize: bodyFontSize, color: bodyColor }, 'body');
    const align = getAlignClasses(textAlign);
    const fullImage = isFullImage(imageDisplay);

    return (
        <section ref={ref} className={cn('bg-background py-10 lg:py-14', getMarginBottomClass(marginBottom))}>
            <div className="section-container px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    {/* Copy side */}
                    <div
                        className={cn(
                            'flex flex-col gap-6 df-reveal',
                            align?.text,
                            align?.items,
                            imageRight ? 'order-2 lg:order-1' : 'order-2',
                            inView && 'df-reveal--show'
                        )}
                        style={{ transitionDelay: inView ? '400ms' : '0ms' }}>
                        {eyebrow && (
                            <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{eyebrow}</p>
                        )}
                        {title && (
                            <h2
                                style={{ fontFamily: 'var(--font-display)', ...heading.style }}
                                className={cn(
                                    'text-3xl lg:text-4xl font-light leading-snug text-foreground',
                                    heading.className
                                )}>
                                {title}
                            </h2>
                        )}
                        {bodyCopy && (
                            <p
                                style={bodyText.style}
                                className={cn(
                                    'text-sm lg:text-base text-muted-foreground leading-relaxed',
                                    bodyText.className
                                )}>
                                {bodyCopy}
                            </p>
                        )}
                        {((ctaOneText && ctaOneLink) || (ctaTwoText && ctaTwoLink)) && (
                            <div className={cn('flex flex-wrap gap-3', align?.justify)}>
                                {ctaOneText && ctaOneLink && (
                                    <Button
                                        asChild
                                        className="df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2">
                                        <Link to={ctaOneLink}>{ctaOneText}</Link>
                                    </Button>
                                )}
                                {ctaTwoText && ctaTwoLink && (
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-none h-12 min-w-[124px] px-6 py-3 gap-2">
                                        <Link to={ctaTwoLink}>{ctaTwoText}</Link>
                                    </Button>
                                )}
                            </div>
                        )}
                        {legalText && (
                            <p className="text-[11px] text-muted-foreground/70 leading-relaxed">{legalText}</p>
                        )}
                    </div>

                    {/* Image side */}
                    {image?.url && (
                        <div
                            className={cn(
                                'relative overflow-hidden rounded-ui bg-muted',
                                !fullImage && 'aspect-square',
                                imageRight ? 'order-1 lg:order-2' : 'order-1'
                            )}>
                            <DynamicImage
                                src={image.url}
                                alt={imageAlt ?? ''}
                                widths={{ base: 768, lg: 720 }}
                                className={fullImage ? 'block w-full' : 'absolute inset-0 w-full h-full'}
                                imageProps={{
                                    className: cn(
                                        'transition-transform duration-700 hover:scale-105',
                                        fullImage ? FULL_IMAGE_CLASSES : 'w-full h-full object-cover'
                                    ),
                                }}
                            />
                            {/* RGBARRY-CUSTOMIZATION: stretched link makes the image clickable to the
                                primary CTA destination, independent of the CTA buttons on the copy side */}
                            {ctaOneText && ctaOneLink && <StretchedLink to={ctaOneLink} />}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
